import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

export interface AiSource {
	type: 'faq' | 'document' | 'website' | 'note' | 'product';
	refId?: string;
	title: string;
	url?: string;
	score?: number;
	via?:
		| 'pre-retrieval'
		| 'search-faq'
		| 'search-business-knowledge'
		| 'record-source-use:pre-retrieval'
		| 'record-source-use:search-faq'
		| 'record-source-use:search-business-knowledge'
		| 'record-source-use:search-products'
		| 'answer-match:pre-retrieval'
		| 'answer-match:search-faq';
}

interface AiMeta {
	agentName?: string;
	toolCalls?: any[];
	sources?: AiSource[];
	// Draft mode: what the AI wants the team to know or check. Never sent to the customer.
	staffNote?: string;
	// Draft mode: when the website pages behind the draft were read (ISO).
	websiteCheckedAt?: string;
}

interface AiDraft {
	_id?: string;
	draftId?: string;
	_customerConversation: string;
	// '' when the AI could not answer and left only aiMeta.staffNote.
	draftText: string;
	aiMeta?: AiMeta;
	expiresAt?: string;
	createdAt?: string;
	updatedAt?: string;
}

// 'draft_ready' follows a Draft-mode reply (message-consumer); the inbox treats it like 'idle'.
type AiStatus = 'idle' | 'thinking' | 'skipped' | 'draft_ready';
type AiMode = 'auto' | 'assist' | 'delayed' | 'off';

interface ConversationWithAiDraft {
	_id: string;
	aiDraft?: AiDraft | null;
}

export interface EscalationData {
	_customerConversation: string;
	reason?: string;
	summary?: string;
	createdAt?: string;
}

interface AiEmployeeState {
	statusByConversation: Record<string, AiStatus>;
	modeByConversation: Record<string, AiMode | undefined>;
	draftByConversation: Record<string, AiDraft | null>;
	escalationsByConversation: Record<string, EscalationData>;
	pendingEscalationCount: number;
}

const initialState: AiEmployeeState = {
	statusByConversation: {},
	modeByConversation: {},
	draftByConversation: {},
	escalationsByConversation: {},
	pendingEscalationCount: 0,
};

const aiEmployeeSlice = createSlice({
	name: 'aiEmployee',
	initialState,
	reducers: {
		setAiStatus(
			state,
			action: PayloadAction<{
				_customerConversation: string;
				status: AiStatus;
				mode?: AiMode;
			}>
		) {
			const { _customerConversation, status, mode } = action.payload;
			state.statusByConversation[_customerConversation] = status;
			if (mode) {
				state.modeByConversation[_customerConversation] = mode;
			} else if (status !== 'thinking') {
				delete state.modeByConversation[_customerConversation];
			}
		},
		setAiDraft(
			state,
			action: PayloadAction<AiDraft>
		) {
			const { _customerConversation } = action.payload;
			state.draftByConversation[_customerConversation] = {
				...action.payload,
			};
		},
		clearAiDraft(
			state,
			action: PayloadAction<{ _customerConversation: string }>
		) {
			const { _customerConversation } = action.payload;
			state.draftByConversation[_customerConversation] = null;
		},
		hydrateAiDraftsFromConversations(
			state,
			action: PayloadAction<ConversationWithAiDraft[]>
		) {
			for (const conversation of action.payload || []) {
				if (!conversation?._id || !('aiDraft' in conversation)) continue;
				// A note-only draft (the AI couldn't answer) still shows, as the amber note card.
				if (
					conversation.aiDraft?.draftText?.trim() ||
					conversation.aiDraft?.aiMeta?.staffNote?.trim()
				) {
					state.draftByConversation[conversation._id] = {
						...conversation.aiDraft,
						_customerConversation:
							conversation.aiDraft._customerConversation ||
							conversation._id,
					};
				} else {
					state.draftByConversation[conversation._id] = null;
				}
			}
		},
		setAiEscalation(
			state,
			action: PayloadAction<EscalationData>
		) {
			const { _customerConversation } = action.payload;
			state.escalationsByConversation[_customerConversation] =
				action.payload;
			state.pendingEscalationCount = Object.keys(
				state.escalationsByConversation
			).length;
		},
		removeAiEscalation(
			state,
			action: PayloadAction<{ _customerConversation: string }>
		) {
			const { _customerConversation } = action.payload;
			delete state.escalationsByConversation[_customerConversation];
			state.pendingEscalationCount = Object.keys(
				state.escalationsByConversation
			).length;
		},
		setPendingEscalationCount(
			state,
			action: PayloadAction<number>
		) {
			state.pendingEscalationCount = action.payload;
		},
		// Replaces the whole map with what the server says is open, so a
		// hand-off closed elsewhere (a teammate's reply or Resolve) loses its
		// badge, and the count matches the map.
		setEscalations(state, action: PayloadAction<EscalationData[]>) {
			const map: Record<string, EscalationData> = {};
			for (const escalation of action.payload || []) {
				if (!escalation?._customerConversation) continue;
				map[escalation._customerConversation] = escalation;
			}
			state.escalationsByConversation = map;
			state.pendingEscalationCount = Object.keys(map).length;
		},
	},
});

export const {
	setAiStatus,
	setAiDraft,
	clearAiDraft,
	hydrateAiDraftsFromConversations,
	setAiEscalation,
	removeAiEscalation,
	setPendingEscalationCount,
	setEscalations,
} = aiEmployeeSlice.actions;

/**
 * The server's list of open escalations, with any change made on this screen
 * while the request was out kept on top: an 'ai_escalation' event that arrived
 * meanwhile is newer than the answer, and so is a badge cleared because the
 * hand-off closed. Entries are compared by identity, so only the ones that
 * changed since `before` count.
 */
export function mergeFetchedEscalations(
	before: Record<string, EscalationData>,
	current: Record<string, EscalationData>,
	fetched: EscalationData[]
): EscalationData[] {
	const merged = new Map<string, EscalationData>();
	for (const escalation of fetched) {
		if (escalation?._customerConversation) {
			merged.set(escalation._customerConversation, escalation);
		}
	}
	const ids = new Set([...Object.keys(before), ...Object.keys(current)]);
	for (const id of ids) {
		if (before[id] === current[id]) continue;
		if (!current[id]) {
			merged.delete(id);
			continue;
		}
		// A 'conversation' event sets the badge without the reason, so keep the server's.
		const known = Object.entries(current[id]).filter(([, value]) => value !== undefined);
		merged.set(id, { ...merged.get(id), ...Object.fromEntries(known) } as EscalationData);
	}
	return [...merged.values()];
}

// Values that say a conversation has no open hand-off. Anything else, including
// a payload without the field, says nothing and never clears a badge.
const CLOSED_HANDOFF = ['resolved', 'none'];

/**
 * Removes the 'Escalated' badge when a conversation arrives saying its hand-off
 * is closed, for example after a team member's reply closed it.
 */
export const clearEscalationIfClosed =
	(conversation?: { _id?: string; handoffStatus?: string } | null) =>
	(dispatch: any, getState: any) => {
		if (
			!conversation?._id ||
			!CLOSED_HANDOFF.includes(conversation.handoffStatus as string)
		) {
			return;
		}
		const _customerConversation = String(conversation._id);
		if (!getState().aiEmployee?.escalationsByConversation?.[_customerConversation]) {
			return;
		}
		dispatch(
			aiEmployeeSlice.actions.removeAiEscalation({ _customerConversation })
		);
	};

export const fetchPendingEscalations =
	() => async (dispatch: any, getState: any) => {
		try {
			const businessId = getState().auth?._id;
			if (!businessId) return;
			const before = getState().aiEmployee?.escalationsByConversation ?? {};

			// The server sends at most the newest 100 open tickets.
			const { data } = await axios.get(
				`/api/ai-employee/escalations/${businessId}?status=pending`
			);

			const tickets = data?.tickets ?? [];
			const fetched: EscalationData[] = tickets.map((ticket: any) => ({
				_customerConversation: ticket._customerConversation,
				reason: ticket.reason,
				summary: ticket.summary,
				createdAt: ticket.createdAt,
			}));

			const current = getState().aiEmployee?.escalationsByConversation ?? {};
			dispatch(
				aiEmployeeSlice.actions.setEscalations(
					mergeFetchedEscalations(before, current, fetched)
				)
			);
		} catch {
			/* AI service may be unavailable — fail silently */
		}
	};

export default aiEmployeeSlice.reducer;
